import * as React from "react";

import {
  Checkbox,
  FormControlLabel,
  FormGroup,
  Radio,
  RadioGroup,
  TextField,
} from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import type { createDeviceForm } from "../../../lib/apis/dtos/request/device.request";
import { createDevice } from "../../../lib/apis/v1/device.service";
import FailSnackbar from "../../ui/snackbar/fail.component";
import { getQueryClient } from "../../../config/queryClient.config";
import SuccessSnackbar from "../../ui/snackbar/success.component";
import ConfirmModal from "../confirm.modal";
import Button from "../../ui/button";
import { cn } from "../../../utils/cn.utils";
import type { ModalComponentProps } from "../../../types/modal.types";

const DEVICE_MODEL = [
  { label: "아이폰", value: "IPHONE" },
  { label: "갤럭시", value: "GALAXY" },
  { label: "기타", value: "ETC" },
];

const CATEGORY_TYPE = [
  { label: "주요폰", value: "main" },
  { label: "중저가폰", value: "low" },
  { label: "키즈폰", value: "kids" },
];

export default function DeviceCreateModal(props: ModalComponentProps) {
  const { id, onClose } = props;

  const [form, setForm] = React.useState<createDeviceForm>({
    deviceCode: "",
    deviceName: "",
    category: "",
    mainCheck: false,
    lowCheck: false,
    kidsCheck: false,
    sizeAvailable: [undefined] as Array<string | undefined>,
    isDefault: false,
    isOnEvent: false,
    eventTag: "",
    eventMessage: ""
  });

  const deviceMutation = useMutation({
    mutationFn: (form: createDeviceForm) => createDevice(form),
    onSuccess: (res) => {
      const { error, message } = res || {};
      if (error) {
        return FailSnackbar(`기종 생성 실패 (${message})`)
      }
      getQueryClient().invalidateQueries({ queryKey: ["device-list"] });
      SuccessSnackbar("기종 생성 완료");
      onClose?.();
    },
  });

  return (
    <ConfirmModal
      title="기종 생성"
      rightButton={{
        title: "생성",
        disabled: !form.deviceName || !form.deviceCode || !form.category || !form.sizeAvailable[0],
        onClick: () => {
          const cleanedSizes = form.sizeAvailable.filter(Boolean).sort();
          const _form: createDeviceForm = {
            ...form,
            sizeAvailable: cleanedSizes,
          };
          const uniqueDeplicates = [...new Set(form.sizeAvailable)];
          if (uniqueDeplicates.length !== form.sizeAvailable.length) {
            return FailSnackbar("기종 생성 실패 (중복되는 용량이 있습니다.)");
          }

          deviceMutation.mutate(_form);
        },
      }}
      id={id}
      onClose={onClose}
    >
      <p className="text-[16px]">카테고리 구분 (복수 선택 가능)</p>
      <FormGroup className="px-[9px]" row>
        {CATEGORY_TYPE.map(({ label, value }, index) => {
          const key = `${value}Check` as keyof typeof form;
          return (
            <FormControlLabel
              key={index}
              control={
                <Checkbox
                  onClick={() =>
                    setForm((state) => ({
                      ...state,
                      [`${value}Check`]: !state[key],
                    }))
                  }
                />
              }
              label={label}
            />
          );
        })}
      </FormGroup>
      <p className="text-[16px] mt-[16px]">모델 구분</p>
      <RadioGroup
        className="px-[9px]"
        row
        aria-labelledby="demo-radio-bujttons-group-label"
        name="radio-buttons-group"
      >
        {DEVICE_MODEL.map(({ label, value }, index) => (
          <FormControlLabel
            key={index}
            value={value}
            control={
              <Radio
                onClick={() =>
                  setForm((state) => ({ ...state, category: value }))
                }
              />
            }
            label={label}
          />
        ))}
      </RadioGroup>
      <div className="mt-[16px] flex flex-col items-center justify-start gap-4">
        <TextField
          className="w-[552px] h-[52px]"
          label="기종명*"
          placeholder="기종명 입력"
          onChange={(e) =>
            setForm((state) => ({ ...state, deviceName: e.target.value }))
          }
          slotProps={{ inputLabel: { shrink: true } }}
        />
        <TextField
          className="w-[552px] h-[52px]"
          label="모델 고유번호*"
          placeholder="모델 고유번호 입력"
          onChange={(e) =>
            setForm((state) => ({ ...state, deviceCode: e.target.value }))
          }
          slotProps={{ inputLabel: { shrink: true } }}
        />
        {form.sizeAvailable.map((_, index) => {
          return (
            <div className="flex-row gap-3" key={index}>
              <TextField
                className={cn("h-[52px]", index !== 0 ? "w-[516px]" : "w-[552px]")}
                label="용량(GB)"
                placeholder="용량 입력 (ex. 256GB)"
                value={form.sizeAvailable[index]}
                onChange={(e) =>
                  setForm((state) => {
                    const updated = [...state.sizeAvailable];
                    updated[index] = e.target.value as unknown as string;
                    return {
                      ...state,
                      sizeAvailable: updated,
                    };
                  })
                }
                slotProps={{ inputLabel: { shrink: true } }}
              />
              {index !== 0 && (
                <img className="cursor-pointer" onClick={() => {
                  setForm((state) => ({ ...state, sizeAvailable: state.sizeAvailable.filter((_, i) => i !== index) }))
                }} width={24} height={24} alt="" src={"/static/icons/icon_cancel_fill.svg"} />
              )}
            </div>

          );
        })}
        <Button
          label="용량 추가"
          className="w-full"
          variant={"outline"}
          onClick={() =>
            setForm((state) => ({
              ...state,
              sizeAvailable: [...state.sizeAvailable, undefined],
            }))
          }
        />
      </div>
    </ConfirmModal>
  );
};
